import React, { useState } from 'react';
import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colorPrimary } from '../../Styles/Colors';

const StyledTab = styled(Button)(({ theme, isActive, variant }) => ({
  minHeight: '32px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  textTransform: 'none',
  transition: 'all 0.2s ease-in-out',
  padding: '6px 16px',
  
 
  ...(variant === 'default' && {
    backgroundColor: isActive ? colorPrimary: 'transparent',
    color: isActive ? '#FFFFFF' : '#424242',
    border: 'none',
    '&:hover': {
      backgroundColor: isActive ? colorPrimary : '#F5F5F5',
    },
  }),
  
  // Outlined variant
  ...(variant === 'outlined' && {
    backgroundColor: isActive ? '#F3E5F5' : 'transparent',
    color: isActive ? colorPrimary : '#424242',
    border: `1px solid ${isActive ? colorPrimary: '#E0E0E0'}`,
    '&:hover': {
      backgroundColor: isActive ? '#E1BEE7' : '#F5F5F5',
      borderColor: isActive ? colorPrimary: '#BDBDBD',
    },
  }),
  
  // Underline variant
  ...(variant === 'underline' && {
    backgroundColor: 'transparent',
    color: isActive ? colorPrimary: '#424242',
    border: 'none',
    borderBottom: `2px solid ${isActive ? colorPrimary: 'transparent'}`,
    borderRadius: '0',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  }),
}));

const TabGroup = styled(Box)(({ variant }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: variant === 'underline' ? '0px' : '4px',
  ...(variant === 'outlined' && {
    padding: '4px',
    backgroundColor: '#F5F5F5',
    borderRadius: '12px',
  }),
}));

const Tab = ({ 
  label = "Tab",
  isActive = false,
  variant = "default", // default, outlined, underline
  onClick,
  icon,
  disabled = false,
  sx = {},
  style = {},
  ...props 
}) => {
  const handleClick = () => {
    if (onClick && !disabled) {
      onClick();
    }
  };

  return (
    <StyledTab
      isActive={isActive}
      variant={variant}
      onClick={handleClick}
      disabled={disabled}
      startIcon={icon}
      sx={{ ...style, ...sx }}
      {...props}
    >
      {label}
    </StyledTab>
  );
};

// Tab Group Component
export const Tabs = ({ 
  tabs = [],
  activeTab = 0,
  onTabChange,
  variant = "default",
  style,
  ...props 
}) => {
  const [activeIndex, setActiveIndex] = useState(activeTab);

  const handleTabClick = (index, tab) => {
    setActiveIndex(index);
    if (onTabChange) {
      onTabChange(index, tab);
    }
  };

  return (
    <TabGroup variant={variant} style={style} {...props}>
      {tabs.map((tab, index) => (
        <Tab
          key={typeof tab === 'string' ? tab : tab.value || index}
          label={typeof tab === 'string' ? tab : tab.label}
          isActive={activeIndex === index}
          variant={variant}
          icon={typeof tab === 'object' ? tab.icon : undefined}
          disabled={typeof tab === 'object' ? tab.disabled : false}
          onClick={() => handleTabClick(index, tab)}
        />
      ))}
    </TabGroup>
  );
};

export default Tab;
